import { 
  collection, 
  doc, 
  setDoc, 
  getDoc,
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase/config';
import { GameResult } from '../models/GameResult';
import { User } from '../models/User';

export class GameResultService {
  private readonly COLLECTION_NAME = 'gameResults';
  private readonly USERS_COLLECTION = 'users';
  
  /**
   * ゲーム結果を保存
   */
  async saveGameResult(
    user: User,
    completionTime: number,
    roundNumber: number,
    boardSeed: string,
    hintPenalty: number,
    greenChipsUsed: number
  ): Promise<void> {
    try {
      const gameResult: GameResult = {
        userId: user.uid,
        userDisplayName: user.displayName,
        completionTime,
        roundNumber,
        boardSeed,
        hintPenalty,
        greenChipsUsed,
        completedAt: serverTimestamp() as Timestamp,
        isAnonymous: user.isAnonymous,
      };
      
      // ゲーム結果をFirestoreに保存
      const docRef = doc(collection(db, this.COLLECTION_NAME));
      await setDoc(docRef, gameResult);
      
      // ユーザー統計も更新
      await this.updateUserStats(user.uid, completionTime);
      
      console.log('Game result saved successfully');
    } catch (error) {
      console.error('Error saving game result:', error);
      throw error;
    }
  }
  
  /**
   * デイリーランキングを取得（今日の結果）
   */
  async getDailyRanking(limitCount: number = 10): Promise<GameResult[]> {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      console.log('Daily ranking date range:', {
        from: today.toISOString(),
        to: tomorrow.toISOString(),
      });
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('completedAt', '>=', Timestamp.fromDate(today)),
        where('completedAt', '<', Timestamp.fromDate(tomorrow)),
        orderBy('completedAt'),
        limit(limitCount * 3)  // 多めに取得してソート
      );
      
      const snapshot = await getDocs(q);
      const results: GameResult[] = [];
      
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as GameResult);
      });
      
      // completionTimeでソートしてから返す
      results.sort((a, b) => a.completionTime - b.completionTime);
      
      return results.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting daily ranking:', error);
      return [];
    }
  }
  
  /**
   * 週間ランキングを取得（今週の結果）
   */
  async getWeeklyRanking(limitCount: number = 10): Promise<GameResult[]> {
    try {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
      monday.setHours(0, 0, 0, 0);
      
      const nextMonday = new Date(monday);
      nextMonday.setDate(monday.getDate() + 7);
      
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('completedAt', '>=', Timestamp.fromDate(monday)),
        where('completedAt', '<', Timestamp.fromDate(nextMonday)),
        orderBy('completedAt'),
        limit(limitCount * 3)  // 多めに取得してソート
      );
      
      const snapshot = await getDocs(q);
      const results: GameResult[] = [];
      
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as GameResult);
      });
      
      // completionTimeでソートしてから返す
      results.sort((a, b) => a.completionTime - b.completionTime);
      
      return results.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting weekly ranking:', error);
      return [];
    }
  }
  
  /**
   * 全期間ランキングを取得
   */
  async getAllTimeRanking(limitCount: number = 10): Promise<GameResult[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('completionTime', 'asc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const results: GameResult[] = [];
      
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as GameResult);
      });
      
      return results;
    } catch (error) {
      console.error('Error getting all-time ranking:', error);
      return [];
    }
  }
  
  /**
   * 特定ユーザーの最新結果を取得
   */
  async getUserRecentResults(userId: string, limitCount: number = 10): Promise<GameResult[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('completedAt', 'desc'),
        limit(limitCount)
      );
      
      const snapshot = await getDocs(q);
      const results: GameResult[] = [];
      
      snapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() } as GameResult);
      });
      
      return results;
    } catch (error) {
      console.error('Error getting user results:', error);
      return [];
    }
  }
  
  /**
   * ユーザー統計を更新
   */
  private async updateUserStats(userId: string, completionTime: number): Promise<void> {
    try {
      const userRef = doc(db, this.USERS_COLLECTION, userId);
      const userDoc = await getDoc(userRef);

      // LocalStorageからニックネームを取得
      let nickname: string | null = null;
      try {
        nickname = localStorage.getItem('chimdon_auth_nickname');
      } catch (e) {
        // LocalStorage読み込み失敗は無視
      }

      if (!userDoc.exists()) {
        // ユーザードキュメントが存在しない場合は新規作成
        const newUserData: any = {
          uid: userId,
          stats: {
            totalGames: 1,
            bestTime: completionTime,
            recentTimes: [completionTime],
            averageTime10: completionTime,
            averageTime30: completionTime,
            averageTime100: completionTime,
          },
          lastLoginAt: serverTimestamp(),
        };

        // ニックネームがあれば保存
        if (nickname) {
          newUserData.nickname = nickname;
        }

        await setDoc(userRef, newUserData);
        return;
      }
      
      const userData = userDoc.data() as User;
      const stats = userData.stats || {
        totalGames: 0,
        bestTime: null,
        recentTimes: [],
        averageTime10: null,
        averageTime30: null,
        averageTime100: null,
      };
      
      // 統計を更新
      const newTotalGames = (stats.totalGames || 0) + 1;
      const newBestTime = stats.bestTime === null ? completionTime : Math.min(stats.bestTime, completionTime);
      
      // 最近のタイムを更新（最大100件保持）
      const newRecentTimes = [completionTime, ...(stats.recentTimes || [])].slice(0, 100);
      
      // 平均タイムを計算
      const calculateAverage = (times: number[], count: number) => {
        const relevantTimes = times.slice(0, Math.min(count, times.length));
        if (relevantTimes.length === 0) return null;
        return Math.floor(relevantTimes.reduce((a, b) => a + b, 0) / relevantTimes.length);
      };
      
      const newStats = {
        totalGames: newTotalGames,
        bestTime: newBestTime,
        recentTimes: newRecentTimes,
        averageTime10: calculateAverage(newRecentTimes, 10),
        averageTime30: calculateAverage(newRecentTimes, 30),
        averageTime100: calculateAverage(newRecentTimes, 100),
      };
      
      // Firestoreに保存
      const updateData: any = {
        ...userData,
        stats: newStats,
        lastLoginAt: serverTimestamp(),
      };

      // ニックネームがあれば更新
      if (nickname) {
        updateData.nickname = nickname;
      }

      await setDoc(userRef, updateData, { merge: true });
      
    } catch (error) {
      console.error('Error updating user stats:', error);
      // 統計の更新に失敗してもゲーム結果の保存は続行
    }
  }
}